import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { graphql } from 'babel-plugin-relay/macro'

import styled from 'styled-components'
import { InputBase, Link, Paper, Tab, Tabs, Typography } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { Skeleton, Pagination } from '@material-ui/lab'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { AiCallLogsQuery } from '../../__generated__/AiCallLogsQuery.graphql'
import { useLocation } from 'react-router'
import { parse } from 'qs'
import { debounce } from 'lodash'
import useWorkspaceNavigate from '../../hooks/useWorkspaceNavigate'
import StatusChip, { ChipTone } from '../../components/StatusChip'
import { Clamped, CopyableText, FullText, useCopy } from '../../components/ClampedText'

const RootPaper = styled(Paper)``
const TitleContainer = styled.div`
  padding: 16px;
`
const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px;
`
const TableWrapper = styled.div`
  overflow-x: auto;
`
// Fixed layout so the columns divide the width that is there rather than the width the
// longest value would like: the percentages below always add up to the table, so the page
// never scrolls sideways however long a model name or a failure reason runs.
const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`
const Col = styled.col<{ $width: string }>`
  width: ${(props) => props.$width};
`
const Th = styled.th`
  text-align: left;
  padding: 12px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Td = styled.td`
  padding: 12px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  vertical-align: top;
  /* Container numbers have no spaces to break at, and a fixed layout would let them run
     under the next column rather than widening the one they are in. */
  overflow-wrap: anywhere;
`
const Muted = styled.span`
  color: rgba(0, 0, 0, 0.5);
`
// Unresolved entities read as muted text rather than a link, but are still worth copying:
// that is the number to search on when working out why nothing was matched.
const MutedText = styled(CopyableText)`
  color: rgba(0, 0, 0, 0.5);
`
const ErrorText = styled(CopyableText)`
  color: #b00020;
  font-size: 0.8rem;
  margin-top: 2px;
`
const EntityLink = styled(Link)`
  display: inline-block;
  max-width: 100%;
`
const ToggleLink = styled.button`
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  padding: 0;
  font-size: 0.8rem;
`
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px;
`
const TitleBlock = styled.div`
  min-width: 0;
`
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  width: 320px;
  max-width: 100%;
  max-height: 44px;
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  background: ${(props) => props.theme.palette.background.paper};
`
const SearchInput = styled(InputBase)`
  flex: 1;
  & > input {
    padding: 10px 4px;
    text-indent: 8px;
  }
`
const SearchIconBox = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px;
  color: ${(props) => props.theme.palette.text.secondary};
  border-left: 1px solid ${(props) => props.theme.palette.divider};
`
const Payload = styled.pre`
  margin: 8px 0 0;
  padding: 8px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  font-size: 0.75rem;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`

const TYPE_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Triage', value: 'invoice-triage' },
  { label: 'Classify', value: 'invoice-classify' },
  { label: 'Extraction', value: 'invoice-extraction' },
]

const statusTone = (status: string): ChipTone => {
  if (status === 'success') return 'success'
  if (status === 'failed') return 'danger'
  return 'neutral'
}

const AiCallLogs: React.FC = () => {
  const location = useLocation()
  const wsNavigate = useWorkspaceNavigate()
  const queryVariables = parse(location.search, { ignoreQueryPrefix: true })
  const page = Number.parseInt(queryVariables.page as string) || 1
  const perPage = Number.parseInt(queryVariables.perPage as string) || 20
  const type = (queryVariables.type as string) || 'all'
  const search = (queryVariables.search as string) || ''

  // Typing updates the field immediately but the URL (and so the query) only after a
  // pause - one request per search, not one per keystroke.
  const [searchInput, setSearchInput] = useState(search)
  const pushSearch = useMemo(
    () => debounce((value: string) => wsNavigate.relative({ type, search: value || undefined, page: 1 }), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type]
  )
  useEffect(() => pushSearch.cancel, [pushSearch])
  // Keeps the field honest when the URL changes from elsewhere, e.g. the back button.
  useEffect(() => setSearchInput(search), [search])

  return (
    <RootPaper variant='outlined'>
      <HeaderBar>
        <TitleBlock>
          <Typography variant='h5'>AI Logs</Typography>
          <Typography variant='body1'>Every model call made while reading invoices</Typography>
        </TitleBlock>
        <SearchBox>
          <SearchInput
            fullWidth
            placeholder='Search entity, e.g. MEDU4637582'
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
              pushSearch(e.target.value)
            }}
          />
          <SearchIconBox>
            <SearchIcon fontSize='small' />
          </SearchIconBox>
        </SearchBox>
      </HeaderBar>
      <Tabs
        value={type}
        indicatorColor='primary'
        textColor='primary'
        variant='scrollable'
        scrollButtons='auto'
        onChange={(_, newType) => wsNavigate.relative({ type: newType, search: search || undefined, page: 1 })}
      >
        {TYPE_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
      <Suspense fallback={<AiCallLogsTable.Skeleton />}>
        <AiCallLogsTable
          page={page}
          perPage={perPage}
          type={type}
          search={search}
          onPageChange={(newPage) => wsNavigate.relative({ type, search: search || undefined, page: newPage })}
        />
      </Suspense>
    </RootPaper>
  )
}

interface Params {
  page: number
  perPage: number
  type: string
  search: string
  onPageChange: (page: number) => void
}

const AiCallLogsTable: React.FC<Params> & { Skeleton: React.FC } = ({ page, perPage, type, search, onPageChange }) => {
  const copy = useCopy()
  const data = useLazyLoadQuery<AiCallLogsQuery>(query, {
    page,
    perPage,
    type,
    searchQuery: search || null,
  })
  const [expanded, setExpanded] = useState<string | null>(null)
  const logs = data.aiCallLogList.aiCallLogs ?? []

  if (logs.length === 0) {
    return (
      <TitleContainer>
        <Muted>{search ? `No AI calls found for "${search}".` : 'No AI calls recorded yet.'}</Muted>
      </TitleContainer>
    )
  }

  return (
    <>
      <TableWrapper>
        <Table>
          <colgroup>
            <Col $width='16%' />
            <Col $width='20%' />
            <Col $width='13%' />
            <Col $width='19%' />
            <Col $width='10%' />
            <Col $width='12%' />
            <Col $width='10%' />
          </colgroup>
          <thead>
            <tr>
              <Th>Call</Th>
              <Th>Entity</Th>
              <Th>Model</Th>
              <Th>Status</Th>
              <Th>Duration</Th>
              <Th>Tokens</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.slug}>
                <Td>
                  {log.displayCallType}
                  <div>
                    <ToggleLink type='button' onClick={() => setExpanded(expanded === log.slug ? null : log.slug)}>
                      {expanded === log.slug ? 'hide response' : 'show response'}
                    </ToggleLink>
                  </div>
                  {expanded === log.slug && <Payload>{JSON.stringify(log.responsePayload ?? {}, null, 2)}</Payload>}
                </Td>
                <Td>
                  {log.entityLabel ? (
                    <FullText text={log.entityLabel}>
                      {log.entityUrl ? (
                        <EntityLink href={log.entityUrl} target='_blank' rel='noopener noreferrer'>
                          <Clamped>{log.entityLabel}</Clamped>
                        </EntityLink>
                      ) : (
                        <MutedText onClick={() => copy(log.entityLabel as string)}>{log.entityLabel}</MutedText>
                      )}
                    </FullText>
                  ) : (
                    <Muted>--</Muted>
                  )}
                </Td>
                <Td>{log.model}</Td>
                <Td>
                  <StatusChip $tone={statusTone(log.status)}>{log.status}</StatusChip>
                  {log.failureReason && (
                    <FullText text={log.failureReason}>
                      <ErrorText onClick={() => copy(log.failureReason as string)}>{log.failureReason}</ErrorText>
                    </FullText>
                  )}
                </Td>
                <Td>{log.durationMs != null ? `${(log.durationMs / 1000).toFixed(2)} s` : <Muted>--</Muted>}</Td>
                <Td>
                  {log.promptTokens != null || log.completionTokens != null ? (
                    <>
                      {log.promptTokens ?? 0} in / {log.completionTokens ?? 0} out
                    </>
                  ) : (
                    <Muted>--</Muted>
                  )}
                </Td>
                <Td>{log.createdAt}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      {data.aiCallLogList.paginationMeta && data.aiCallLogList.paginationMeta.totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            shape='rounded'
            count={data.aiCallLogList.paginationMeta.totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
          />
        </PaginationContainer>
      )}
    </>
  )
}

AiCallLogsTable.Skeleton = () => (
  <TitleContainer>
    <Skeleton height={48} />
    <Skeleton height={48} />
    <Skeleton height={48} />
  </TitleContainer>
)

const query = graphql`
  query AiCallLogsQuery($type: String, $searchQuery: String, $page: Int, $perPage: Int) {
    aiCallLogList(type: $type, searchQuery: $searchQuery, page: $page, perPage: $perPage) {
      aiCallLogs {
        slug
        displayCallType
        entityLabel
        entityUrl
        model
        status
        failureReason
        durationMs
        promptTokens
        completionTokens
        responsePayload
        createdAt
      }
      totalCount
      paginationMeta {
        totalPages
      }
    }
  }
`

export default AiCallLogs
